import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plane, Plus, X } from "lucide-react";
import { toast } from "sonner";

const form0 = { od_type: "", from_date: "", to_date: "", half_day: false, place: "", purpose: "" };

export default function MyOnDuty() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(form0);

  const { data: items, isLoading } = useQuery({ queryKey: ["myOnDuty"], queryFn: async () => (await api.get("/my/on-duty")).data });
  const { data: types } = useQuery({ queryKey: ["onDutyTypes"], queryFn: async () => (await api.get("/on-duty/types")).data });

  const apply = useMutation({
    mutationFn: async () => (await api.post("/my/on-duty", { ...form, to_date: form.half_day ? form.from_date : form.to_date })).data,
    onSuccess: () => { toast.success("On Duty request submitted"); qc.invalidateQueries({ queryKey: ["myOnDuty"] }); setOpen(false); setForm(form0); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const withdraw = useMutation({
    mutationFn: async (id) => (await api.delete(`/my/on-duty/${id}`)).data,
    onSuccess: () => { toast.success("Request withdrawn"); qc.invalidateQueries({ queryKey: ["myOnDuty"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isLoading) return <Loading />;

  const toDate = form.half_day ? form.from_date : form.to_date;
  const canSubmit = form.od_type && form.from_date && toDate && form.place.trim().length >= 2 && form.purpose.trim().length >= 10 && !apply.isPending;

  return (
    <div>
      <PageHeader overline="Official Work" title="My On Duty" icon={Plane}
        subtitle="Away on official tour, external training or an official meeting? Apply for On Duty so the day counts as present — not absent or leave. You can apply up to 7 days after the date. It goes to your Reporting Officer, then HR."
        actions={<Button className="bg-navy hover:bg-navy-700 btn-lift" onClick={() => setOpen(true)} data-testid="apply-on-duty-btn"><Plus className="h-4 w-4 mr-1" /> Apply for On Duty</Button>} />

      {(items || []).length === 0 ? (
        <EmptyState icon={Plane} title="No On Duty requests yet" hint="Approved On Duty days count as present and paid, with no check-in needed." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Type</TableHead>
              <TableHead className="overline text-slate-500">Dates</TableHead>
              <TableHead className="overline text-slate-500">Place & Purpose</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead className="overline text-slate-500">Remarks</TableHead>
              <TableHead className="text-right overline text-slate-500"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.id} className="table-row-i" data-testid={`on-duty-${o.id}`}>
                  <TableCell className="font-medium text-navy">{o.od_type}</TableCell>
                  <TableCell className="text-slate-600 text-sm whitespace-nowrap">
                    {o.from_date === o.to_date ? o.from_date : `${o.from_date} → ${o.to_date}`}
                    <span className="block text-xs text-slate-400">{o.half_day ? "Half day" : `${o.days} day${o.days > 1 ? "s" : ""}`}</span>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm min-w-[200px] max-w-[340px] whitespace-normal break-words">
                    <span className="font-medium text-navy block">{o.place}</span>
                    <span className="text-xs text-slate-500">{o.purpose}</span>
                  </TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-slate-500 text-xs min-w-[160px] max-w-[280px] whitespace-normal break-words">{o.remarks || o.ro_remarks || "—"}</TableCell>
                  <TableCell className="text-right">{["Pending_RO", "Pending_HR"].includes(o.status) && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => withdraw.mutate(o.id)} title="Withdraw"><X className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="apply-on-duty-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Apply for On Duty</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Type *</Label>
              <Select value={form.od_type} onValueChange={(v) => setForm({ ...form, od_type: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-white">{(types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Switch checked={form.half_day} onCheckedChange={(v) => setForm({ ...form, half_day: v })} /> Half day only
              <span className="text-xs text-slate-400">(with 4+ hours at work the day counts as full)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{form.half_day ? "Date *" : "From *"}</Label><Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="mt-1" /></div>
              {!form.half_day && <div><Label>To *</Label><Input type="date" value={form.to_date} min={form.from_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="mt-1" /></div>}
            </div>
            <div><Label>Place *</Label><Input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} className="mt-1" placeholder="e.g. AIIMS, New Delhi" /></div>
            <div><Label>Purpose *</Label><Textarea value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="mt-1" rows={3} placeholder="e.g. NTEP training on TB diagnostics, nominated by HO" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!canSubmit} onClick={() => apply.mutate()} data-testid="submit-on-duty">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
